import { create } from "zustand";
import { combine } from "zustand/middleware";
import { useShallow } from "zustand/react/shallow";
import type { BehaviorDocument, BTNode } from "../behavior/types";
import { createNodeId } from "../behavior/types";
import { createPickPlaceTemplate } from "../behavior/templates/pickAndPlace";
import { createGo2WalkTemplate } from "../behavior/templates/go2Walk";
import { createSpotWalkTemplate } from "../behavior/templates/spotWalk";
import {
  applyBehaviorDocumentSceneRules,
  getDefaultSelectedBehaviorId,
} from "../behavior/context/rulesRegistry";
import { createBehaviorRunner } from "../behavior/runner";
import type { TickResult } from "../behavior/actions";
import { cloneSceneSnapshotForBaseline, restoreBaselineSnapshot } from "../behavior/sceneOps";
import { logger } from "../utils/logger";

const LS_KEY = "critter_behaviors_v1";

const runner = createBehaviorRunner();

type BehaviorStoreState = {
  behaviors: Record<string, BehaviorDocument>;
  selectedBehaviorId: string | null;
  isRunning: boolean;
  lastTickMessage: string;
  lastEpisodeOutcome: "success" | "failure" | null;
  activeNodeId: string | null;
  nodeStatuses: Record<string, TickResult>;
};

type BehaviorStoreActions = {
  selectBehavior: (id: string | null) => void;
  duplicateBehavior: (sourceId: string) => string | null;
  newEmptyBehavior: () => string;
  updateDocument: (doc: BehaviorDocument) => void;
  renameBehavior: (id: string, name: string) => void;
  deleteBehavior: (id: string) => void;
  captureBaseline: (behaviorId: string) => void;
  applyBaselineNow: (behaviorId: string) => void;
  exportBehaviorJson: (behaviorId: string) => string | null;
  importBehaviorJson: (json: string) => ImportBehaviorResult;
  startRun: (behaviorId: string) => void;
  stopRun: () => void;
  tickRunner: () => void;
};

type BehaviorStoreSlice = BehaviorStoreState & { actions: BehaviorStoreActions };

type ImportBehaviorResult = { ok: true; id: string } | { ok: false; reason: "parse" | "invalid" };

const KNOWN_KINDS = new Set<string>([
  "sequence",
  "selector",
  "retry",
  "repeat",
  "ifThenElse",
  "servoPickPlace",
  "moveEeToXyz",
  "moveEeByOffset",
  "runTrainedModel",
  "openGripper",
  "closeGripper",
  "goToKeyframe",
  "wait",
  "randomizeObjectPose",
  "setObjectPose",
  "resetToBaseline",
  "markSuccess",
  "markFailure",
  "conditionObjectGrasped",
  "conditionEeNear",
  "conditionObjectNear",
  "conditionTimeElapsed",
]);

const toFiniteNumber = (value: unknown, fallback: number) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const toStringOr = (value: unknown, fallback = "") => {
  return typeof value === "string" ? value : fallback;
};

const normalizeOrientationMode = (value: unknown): "free" | "keepDown" | "alignWithGrasp" => {
  const v = toStringOr(value, "free");
  if (v === "keepDown" || v === "alignWithGrasp") return v;
  return "free";
};

function sanitizeNode(input: unknown, fallbackId: string): BTNode {
  const raw = (input && typeof input === "object" ? input : {}) as Record<string, unknown>;
  const id = toStringOr(raw.id, fallbackId) || fallbackId;
  const kindRaw = toStringOr(raw.kind, "wait");
  const kind = KNOWN_KINDS.has(kindRaw) ? kindRaw : "wait";
  const labelRaw = typeof raw.label === "string" ? raw.label : "";
  const labelPatch = labelRaw.trim().length > 0 ? { label: labelRaw } : null;
  const withLabel = <T extends BTNode>(n: T): T =>
    labelPatch ? ({ ...n, ...labelPatch } as T) : n;

  switch (kind) {
    case "sequence":
    case "selector":
      return withLabel({
        id,
        kind,
        children: Array.isArray(raw.children)
          ? raw.children.filter((v): v is string => typeof v === "string")
          : [],
      });
    case "retry":
      return withLabel({
        id,
        kind,
        maxAttempts: Math.max(1, Math.floor(toFiniteNumber(raw.maxAttempts, 3))),
        child: typeof raw.child === "string" ? raw.child : null,
      });
    case "repeat":
      return withLabel({
        id,
        kind,
        count: Math.max(1, Math.floor(toFiniteNumber(raw.count, 2))),
        child: typeof raw.child === "string" ? raw.child : null,
      });
    case "ifThenElse":
      return withLabel({
        id,
        kind,
        conditionChild: typeof raw.conditionChild === "string" ? raw.conditionChild : null,
        thenChild: typeof raw.thenChild === "string" ? raw.thenChild : null,
        elseChild: typeof raw.elseChild === "string" ? raw.elseChild : null,
      });
    case "servoPickPlace":
      return withLabel({
        id,
        kind,
        pickBodyUuid: toStringOr(raw.pickBodyUuid, ""),
        dropBodyUuid: toStringOr(raw.dropBodyUuid, ""),
        endEffectorRef: toStringOr(raw.endEffectorRef, "gripper_target"),
      });
    case "moveEeToXyz":
      return withLabel({
        id,
        kind,
        x: toFiniteNumber(raw.x, 0),
        y: toFiniteNumber(raw.y, 0.3),
        z: toFiniteNumber(raw.z, 0.2),
        targetBodyUuid: toStringOr(raw.targetBodyUuid, ""),
        settleSeconds: Math.max(0, toFiniteNumber(raw.settleSeconds, 0.45)),
        orientationMode: normalizeOrientationMode(raw.orientationMode),
        orientationStrength: Math.max(0, Math.min(2, toFiniteNumber(raw.orientationStrength, 0.8))),
        endEffectorRef: toStringOr(raw.endEffectorRef, "gripper_target"),
      });
    case "moveEeByOffset":
      return withLabel({
        id,
        kind,
        dx: toFiniteNumber(raw.dx, 0),
        dy: toFiniteNumber(raw.dy, 0.05),
        dz: toFiniteNumber(raw.dz, 0),
        settleSeconds: Math.max(0, toFiniteNumber(raw.settleSeconds, 0.35)),
        orientationMode: normalizeOrientationMode(raw.orientationMode),
        orientationStrength: Math.max(0, Math.min(2, toFiniteNumber(raw.orientationStrength, 0.8))),
        endEffectorRef: toStringOr(raw.endEffectorRef, "gripper_target"),
      });
    case "runTrainedModel":
      return withLabel({
        id,
        kind,
        modelId: toStringOr(raw.modelId, "go2-walk-stagef"),
        durationSeconds: Math.max(0, toFiniteNumber(raw.durationSeconds, 3)),
        commandVx: toFiniteNumber(raw.commandVx, 0.2),
        commandWz: toFiniteNumber(raw.commandWz, 0),
      });
    case "openGripper":
      return withLabel({
        id,
        kind,
        settleSeconds: Math.max(0, toFiniteNumber(raw.settleSeconds, 0.4)),
      });
    case "closeGripper":
      return withLabel({
        id,
        kind,
        settleSeconds: Math.max(0, toFiniteNumber(raw.settleSeconds, 0.4)),
      });
    case "resetToBaseline":
    case "markSuccess":
    case "markFailure":
      return withLabel({ id, kind });
    case "goToKeyframe":
      return withLabel({
        id,
        kind,
        keyframeName: toStringOr(raw.keyframeName, "Scene :: home"),
      });
    case "wait":
      return withLabel({
        id,
        kind,
        seconds: Math.max(0, toFiniteNumber(raw.seconds, 0.5)),
      });
    case "randomizeObjectPose":
      return withLabel({
        id,
        kind,
        bodyUuid: toStringOr(raw.bodyUuid, ""),
        radiusXY: Math.max(0, toFiniteNumber(raw.radiusXY, 0.12)),
        yawRangeDeg: Math.max(0, toFiniteNumber(raw.yawRangeDeg, 45)),
      });
    case "setObjectPose":
      return withLabel({
        id,
        kind,
        bodyUuid: toStringOr(raw.bodyUuid, ""),
        x: toFiniteNumber(raw.x, 0),
        y: toFiniteNumber(raw.y, 0.05),
        z: toFiniteNumber(raw.z, 0),
        qw: toFiniteNumber(raw.qw, 1),
        qx: toFiniteNumber(raw.qx, 0),
        qy: toFiniteNumber(raw.qy, 0),
        qz: toFiniteNumber(raw.qz, 0),
      });
    case "conditionObjectGrasped":
      return withLabel({
        id,
        kind,
        bodyUuid: toStringOr(raw.bodyUuid, ""),
        minHeightY: toFiniteNumber(raw.minHeightY, 0.04),
      });
    case "conditionEeNear":
      return withLabel({
        id,
        kind,
        endEffectorRef: toStringOr(raw.endEffectorRef, "gripper_target"),
        x: toFiniteNumber(raw.x, 0),
        y: toFiniteNumber(raw.y, 0.3),
        z: toFiniteNumber(raw.z, 0),
        targetBodyUuid: toStringOr(raw.targetBodyUuid, ""),
        toleranceM: Math.max(0, toFiniteNumber(raw.toleranceM, 0.05)),
      });
    case "conditionObjectNear":
      return withLabel({
        id,
        kind,
        bodyUuid: toStringOr(raw.bodyUuid, ""),
        x: toFiniteNumber(raw.x, 0),
        y: toFiniteNumber(raw.y, 0.05),
        z: toFiniteNumber(raw.z, 0),
        targetBodyUuid: toStringOr(raw.targetBodyUuid, ""),
        toleranceM: Math.max(0, toFiniteNumber(raw.toleranceM, 0.08)),
      });
    case "conditionTimeElapsed":
      return withLabel({
        id,
        kind,
        seconds: Math.max(0, toFiniteNumber(raw.seconds, 1)),
      });
    default:
      return withLabel({ id, kind: "wait", seconds: 0.5 });
  }
}

function breakCycles(nodes: Record<string, BTNode>) {
  const visited = new Set<string>();
  const stack = new Set<string>();

  const dfs = (id: string) => {
    if (!id || visited.has(id)) return;
    visited.add(id);
    stack.add(id);
    const node = nodes[id];
    if (!node) {
      stack.delete(id);
      return;
    }
    if (node.kind === "sequence" || node.kind === "selector") {
      const pruned: string[] = [];
      node.children.forEach((childId) => {
        if (!nodes[childId]) return;
        if (childId === id || stack.has(childId)) return;
        pruned.push(childId);
        dfs(childId);
      });
      nodes[id] = { ...node, children: pruned };
    } else if (node.kind === "retry" || node.kind === "repeat") {
      if (!node.child || !nodes[node.child] || node.child === id || stack.has(node.child)) {
        nodes[id] = { ...node, child: null };
      } else {
        dfs(node.child);
      }
    } else if (node.kind === "ifThenElse") {
      const normalizeSlot = (slotId: string | null) => {
        if (!slotId || !nodes[slotId] || slotId === id || stack.has(slotId)) return null;
        dfs(slotId);
        return slotId;
      };
      nodes[id] = {
        ...node,
        conditionChild: normalizeSlot(node.conditionChild),
        thenChild: normalizeSlot(node.thenChild),
        elseChild: normalizeSlot(node.elseChild),
      };
    }
    stack.delete(id);
  };

  Object.keys(nodes).forEach((id) => dfs(id));
}

function normalizeBehaviorDocument(input: unknown): BehaviorDocument | null {
  if (!input || typeof input !== "object") return null;
  const raw = input as Record<string, unknown>;
  const rawNodes =
    raw.nodes && typeof raw.nodes === "object" ? (raw.nodes as Record<string, unknown>) : {};
  const normalizedNodes: Record<string, BTNode> = {};
  Object.entries(rawNodes).forEach(([k, node]) => {
    const nodeId = node && typeof node === "object" ? (node as { id?: unknown }).id : undefined;
    const id = toStringOr(nodeId, k) || k || createNodeId();
    normalizedNodes[id] = sanitizeNode(node, id);
  });

  if (Object.keys(normalizedNodes).length === 0) {
    const rootId = createNodeId();
    normalizedNodes[rootId] = { id: rootId, kind: "sequence", children: [] };
  }

  const nodeIds = new Set(Object.keys(normalizedNodes));
  Object.keys(normalizedNodes).forEach((id) => {
    const node = normalizedNodes[id];
    if (node.kind === "sequence" || node.kind === "selector") {
      const children = node.children.filter((childId) => nodeIds.has(childId) && childId !== id);
      normalizedNodes[id] = { ...node, children };
    } else if (node.kind === "retry" || node.kind === "repeat") {
      const child = node.child;
      normalizedNodes[id] = {
        ...node,
        child: child && nodeIds.has(child) && child !== id ? child : null,
      };
    } else if (node.kind === "ifThenElse") {
      const valid = (childId: string | null) =>
        childId && nodeIds.has(childId) && childId !== id ? childId : null;
      normalizedNodes[id] = {
        ...node,
        conditionChild: valid(node.conditionChild),
        thenChild: valid(node.thenChild),
        elseChild: valid(node.elseChild),
      };
    }
  });

  breakCycles(normalizedNodes);

  const nodeKeys = Object.keys(normalizedNodes);
  let rootId = toStringOr(raw.rootId, "");
  if (!rootId || !normalizedNodes[rootId]) {
    rootId = nodeKeys[0];
  }
  if (rootId && normalizedNodes[rootId]) {
    const rootNode = normalizedNodes[rootId];
    if (
      rootNode.kind !== "sequence" &&
      rootNode.kind !== "selector" &&
      rootNode.kind !== "retry" &&
      rootNode.kind !== "repeat" &&
      rootNode.kind !== "ifThenElse"
    ) {
      const wrapId = createNodeId();
      normalizedNodes[wrapId] = { id: wrapId, kind: "sequence", children: [rootId] };
      rootId = wrapId;
    }
  }

  const doc: BehaviorDocument = {
    id: toStringOr(raw.id, createNodeId()),
    name: toStringOr(raw.name, "Imported behavior"),
    rootId,
    nodes: normalizedNodes,
    baselineSnapshot:
      raw.baselineSnapshot === null || raw.baselineSnapshot === undefined
        ? null
        : raw.baselineSnapshot,
    isTemplate: raw.isTemplate === true,
  };
  return doc;
}

function sanitizeBehaviorMap(input: unknown): Record<string, BehaviorDocument> {
  if (!input || typeof input !== "object") return {};
  const out: Record<string, BehaviorDocument> = {};
  Object.values(input as Record<string, unknown>).forEach((doc) => {
    const normalized = normalizeBehaviorDocument(doc);
    if (!normalized) return;
    out[normalized.id] = normalized;
  });
  return out;
}

function loadFromStorage(): Record<string, BehaviorDocument> {
  try {
    const raw = globalThis.localStorage?.getItem(LS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") return sanitizeBehaviorMap(parsed);
  } catch {
    /* ignore */
  }
  return {};
}

function saveToStorage(behaviors: Record<string, BehaviorDocument>) {
  try {
    globalThis.localStorage?.setItem(LS_KEY, JSON.stringify(behaviors));
  } catch {
    /* ignore */
  }
}

function ensureBuiltins(initial: Record<string, BehaviorDocument>) {
  const next = { ...initial };
  const pickPlace = createPickPlaceTemplate();
  const go2Walk = createGo2WalkTemplate();
  const spotWalk = createSpotWalkTemplate();
  next[pickPlace.id] = pickPlace;
  next[go2Walk.id] = go2Walk;
  next[spotWalk.id] = spotWalk;
  return next;
}

function cloneDocWithFreshIds(
  src: BehaviorDocument,
  opts: { newBehaviorId?: string; nameOverride?: string } = {}
): BehaviorDocument {
  const newId = opts.newBehaviorId ?? createNodeId();
  const clone = JSON.parse(JSON.stringify(src)) as BehaviorDocument;
  clone.id = newId;
  if (opts.nameOverride) clone.name = opts.nameOverride;
  clone.isTemplate = false;

  const idMap = new Map<string, string>();
  const remap = (oid: string) => {
    if (!idMap.has(oid)) idMap.set(oid, createNodeId());
    return idMap.get(oid)!;
  };
  const oldIds = Object.keys(clone.nodes);
  const nodes: Record<string, BTNode> = {};
  oldIds.forEach((oid) => {
    const nid = remap(oid);
    const n = { ...clone.nodes[oid], id: nid } as BTNode;
    nodes[nid] = n;
  });
  Object.keys(nodes).forEach((nk) => {
    const n = nodes[nk];
    if (n.kind === "sequence" || n.kind === "selector") {
      nodes[nk] = { ...n, children: n.children.map((c) => remap(c)) };
    } else if ((n.kind === "retry" || n.kind === "repeat") && n.child) {
      nodes[nk] = { ...n, child: remap(n.child) };
    } else if (n.kind === "ifThenElse") {
      nodes[nk] = {
        ...n,
        conditionChild: n.conditionChild ? remap(n.conditionChild) : null,
        thenChild: n.thenChild ? remap(n.thenChild) : null,
        elseChild: n.elseChild ? remap(n.elseChild) : null,
      };
    }
  });
  return { ...clone, rootId: remap(clone.rootId), nodes };
}

function isValidBehaviorDocument(obj: unknown): obj is BehaviorDocument {
  return normalizeBehaviorDocument(obj) != null;
}

const initialBehaviors = ensureBuiltins(loadFromStorage());

export const useBehaviorStore = create<BehaviorStoreSlice>()(
  combine<BehaviorStoreState, { actions: BehaviorStoreActions }>(
    {
      behaviors: initialBehaviors,
      selectedBehaviorId: getDefaultSelectedBehaviorId(initialBehaviors),
      isRunning: false,
      lastTickMessage: "",
      lastEpisodeOutcome: null as "success" | "failure" | null,
      activeNodeId: null,
      nodeStatuses: {} as Record<string, TickResult>,
    } satisfies BehaviorStoreState,
    (set, get) => {
      const typedGet = get as unknown as () => BehaviorStoreSlice;
      return {
        actions: {
          selectBehavior: (id: string | null) =>
            set({ selectedBehaviorId: id, nodeStatuses: {}, activeNodeId: null }),

          duplicateBehavior: (sourceId: string) => {
            const src = get().behaviors[sourceId];
            if (!src) return null;
            const nextDoc = applyBehaviorDocumentSceneRules(
              cloneDocWithFreshIds(src, {
                nameOverride: `${src.name} (copy)`,
              })
            );
            set((s) => {
              const behaviors = { ...s.behaviors, [nextDoc.id]: nextDoc };
              saveToStorage(behaviors);
              return { behaviors, selectedBehaviorId: nextDoc.id };
            });
            return nextDoc.id;
          },

          newEmptyBehavior: () => {
            const id = createNodeId();
            const nRoot = createNodeId();
            const nLeaf = createNodeId();
            const doc: BehaviorDocument = {
              id,
              name: "New behavior",
              rootId: nRoot,
              baselineSnapshot: null,
              nodes: {
                [nRoot]: { id: nRoot, kind: "sequence", children: [nLeaf] },
                [nLeaf]: {
                  id: nLeaf,
                  kind: "wait",
                  seconds: 0.1,
                },
              },
            };
            set((s) => {
              const behaviors = { ...s.behaviors, [id]: doc };
              saveToStorage(behaviors);
              return { behaviors, selectedBehaviorId: id };
            });
            return id;
          },

          updateDocument: (doc: BehaviorDocument) => {
            if (!doc || typeof doc !== "object") return;
            if (typeof doc.id !== "string" || !doc.id) return;
            if (typeof doc.rootId !== "string" || !doc.rootId) return;
            if (!doc.nodes || typeof doc.nodes !== "object") return;
            set((s) => {
              const behaviors = { ...s.behaviors, [doc.id]: doc };
              saveToStorage(behaviors);
              return { behaviors };
            });
          },

          renameBehavior: (id: string, name: string) => {
            const b = get().behaviors[id];
            if (!b) return;
            typedGet().actions.updateDocument({ ...b, name });
          },

          deleteBehavior: (id: string) => {
            set((s) => {
              const { [id]: removed, ...rest } = s.behaviors;
              void removed;
              saveToStorage(rest);
              return {
                behaviors: rest,
                selectedBehaviorId:
                  s.selectedBehaviorId === id
                    ? getDefaultSelectedBehaviorId(rest)
                    : s.selectedBehaviorId,
              };
            });
          },

          captureBaseline: (behaviorId: string) => {
            const b = get().behaviors[behaviorId];
            if (!b) return;
            const snap = cloneSceneSnapshotForBaseline();
            typedGet().actions.updateDocument({ ...b, baselineSnapshot: snap });
          },

          applyBaselineNow: (behaviorId: string) => {
            const b = get().behaviors[behaviorId];
            if (!b?.baselineSnapshot) return;
            restoreBaselineSnapshot(b.baselineSnapshot);
          },

          exportBehaviorJson: (behaviorId: string): string | null => {
            const b = get().behaviors[behaviorId];
            if (!b) return null;
            return JSON.stringify(b, null, 2);
          },

          importBehaviorJson: (json: string): ImportBehaviorResult => {
            let parsed: unknown;
            try {
              parsed = JSON.parse(json);
            } catch (e) {
              logger.warn("[behaviorStore] importBehaviorJson: invalid JSON", e);
              return { ok: false, reason: "parse" };
            }
            if (!isValidBehaviorDocument(parsed)) {
              logger.warn("[behaviorStore] importBehaviorJson: not a behavior document");
              return { ok: false, reason: "invalid" };
            }
            const normalized = normalizeBehaviorDocument(parsed);
            if (!normalized) return { ok: false, reason: "invalid" };
            const nextDoc = cloneDocWithFreshIds(normalized, {
              nameOverride: normalized.name || "Imported behavior",
            });
            set((s) => {
              const behaviors = { ...s.behaviors, [nextDoc.id]: nextDoc };
              saveToStorage(behaviors);
              return { behaviors, selectedBehaviorId: nextDoc.id };
            });
            return { ok: true, id: nextDoc.id };
          },

          startRun: (behaviorId: string) => {
            const b = get().behaviors[behaviorId];
            if (!b) return;
            runner.startRun(b);
            set((s) => ({
              isRunning: true,
              lastTickMessage: "running",
              lastEpisodeOutcome: null,
              activeNodeId: null,
              nodeStatuses:
                s.activeNodeId == null && Object.keys(s.nodeStatuses).length === 0
                  ? s.nodeStatuses
                  : {},
            }));
          },

          stopRun: () => {
            runner.stopRun();
            set({ isRunning: false, lastTickMessage: "stopped", activeNodeId: null });
          },

          tickRunner: () => {
            const { isRunning } = get();
            if (!isRunning) return;
            const out = runner.tick();
            const outcome = out.blackboard.episodeOutcome;
            const tickMessage =
              out.result === "failure" && out.failureReason
                ? `failure: ${out.failureReason}`
                : out.result;
            set({
              lastTickMessage: tickMessage,
              lastEpisodeOutcome: outcome ?? get().lastEpisodeOutcome,
              activeNodeId: out.activeNodeId ?? null,
              nodeStatuses: out.statuses,
            });
            if (out.result === "success" || out.result === "failure") {
              runner.stopRun();
              set({ isRunning: false, activeNodeId: null });
            }
          },
        },
      };
    }
  )
);

export const useBehaviorActions = () => useBehaviorStore((s) => s.actions);
export const useBehaviorsMap = () => useBehaviorStore((s) => s.behaviors);
export const useSelectedBehaviorId = () => useBehaviorStore((s) => s.selectedBehaviorId);
export const useBehaviorRunState = () =>
  useBehaviorStore(
    useShallow((s) => ({
      isRunning: s.isRunning,
      lastTickMessage: s.lastTickMessage,
      lastEpisodeOutcome: s.lastEpisodeOutcome,
      activeNodeId: s.activeNodeId,
    }))
  );
export const useNodeStatuses = () => useBehaviorStore((s) => s.nodeStatuses);
